import { BILLING_NSFW_WORKER_URL } from '../config.js';
import { tokenManager } from './TokenManager.js';

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}[char]));

const postInvoice = async ({ plan, rail }) => {
  const token = await tokenManager.getValidAccessToken();
  if (!token) throw new Error('Please log in again.');
  const response = await fetch(`${BILLING_NSFW_WORKER_URL}/create-crypto-invoice`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ plan, rail }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || 'Could not start crypto checkout');
  return { token, payload };
};

const removeModal = () => {
  document.querySelector('.crypto-rail-modal')?.remove();
};

const dialog = (title, body) => `
  <div class="crypto-rail-dialog" role="dialog" aria-modal="true" aria-labelledby="crypto-rail-title">
    <h2 id="crypto-rail-title">${title}</h2>
    ${body}
    <button type="button" class="crypto-rail-close">Close</button>
  </div>`;

const mountModal = (markup) => {
  removeModal();
  const root = document.createElement('div');
  root.className = 'crypto-rail-modal';
  root.innerHTML = markup;
  document.body.appendChild(root);
  root.querySelector('.crypto-rail-close')?.focus();
  return root;
};

const bindClose = (root, onClose) => {
  const close = () => {
    root.remove();
    onClose();
  };
  root.querySelector('.crypto-rail-close')?.addEventListener('click', close);
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
  return close;
};

const showUsdcInvoice = ({ payload, token, button }) => {
  const root = mountModal(dialog('USDC on Base', `
    <p>Send exactly <strong>${escapeHtml(payload.amountUsdc)} USDC</strong> on Base. Any other coin or network is not added.</p>
    <p class="crypto-rail-address" tabindex="0">${escapeHtml(payload.address)}</p>
    <button type="button" class="plan-button crypto-rail-copy">Copy address</button>
    <p class="crypto-rail-status" role="status">Waiting for the transfer.</p>
  `));
  let timer = 0;
  bindClose(root, () => {
    window.clearInterval(timer);
    if (button) button.disabled = false;
  });
  root.querySelector('.crypto-rail-copy')?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(payload.address);
      const status = root.querySelector('.crypto-rail-status');
      if (status) status.textContent = 'Address copied.';
    } catch {
      const status = root.querySelector('.crypto-rail-status');
      if (status) status.textContent = 'Select the address and copy it.';
    }
  });
  const poll = async () => {
    const response = await fetch(`${BILLING_NSFW_WORKER_URL}/crypto-invoice/${payload.invoiceId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const invoice = await response.json().catch(() => null);
    if (!response.ok) return;
    const status = root.querySelector('.crypto-rail-status');
    if (invoice.status === 'settled') {
      window.clearInterval(timer);
      window.location.href = '/account.html?checkout=crypto';
      return;
    }
    if (invoice.status === 'expired' || invoice.status === 'late_payment') {
      window.clearInterval(timer);
      if (status) status.textContent = 'This invoice expired. Start checkout again. A late transfer is not credited automatically.';
    }
  };
  timer = window.setInterval(() => {
    poll().catch(() => {});
  }, 4000);
};

const chooseRail = (plan) => new Promise((resolve) => {
  const root = mountModal(dialog('How do you want to pay?', `
    <button type="button" class="plan-button" data-rail="btc">Bitcoin</button>
    <button type="button" class="plan-button" data-rail="usdc">USDC on Base</button>
  `));
  bindClose(root, () => resolve(null));
  root.querySelectorAll('[data-rail]').forEach((choice) => {
    choice.addEventListener('click', () => {
      const rail = choice.getAttribute('data-rail');
      root.remove();
      resolve(rail === 'btc' || rail === 'usdc' ? rail : null);
    });
  });
  root.dataset.plan = plan;
});

export const startCryptoCheckout = async ({ plan, button }) => {
  const rail = await chooseRail(plan);
  if (!rail) return;
  if (button) button.disabled = true;
  try {
    const { token, payload } = await postInvoice({ plan, rail });
    if (rail === 'btc') {
      if (!payload?.invoiceUrl) throw new Error('Could not start crypto checkout');
      window.location.href = payload.invoiceUrl;
      return;
    }
    if (!payload?.address || !payload?.invoiceId) throw new Error('Could not start crypto checkout');
    showUsdcInvoice({ payload, token, button });
  } catch (error) {
    if (button) button.disabled = false;
    throw error;
  }
};
